import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IVendedor } from '@model/Vendedor';
import { IVendedorEditar, useVendedorService } from '@services/VendedorService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { VendedorEditarForm } from './VendedorEditarForm';

export type VendedorEditarModalProps = {
    modal: IModalWindow
    vendedor: IVendedor
    onVendedorAtualizado: (vendedor: AxiosResponse<IVendedor>) => void
}

export function VendedorEditarModal ({ modal, vendedor, onVendedorAtualizado }: VendedorEditarModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const form = useForm<IVendedorEditar>({
        nome: useField(vendedor.nome),
        cpf: useField(vendedor.cpf),
        telefone: useField(vendedor.telefone),
    })

    const validation = useValidation<IVendedorEditar>({
        nome: yup.string().trim().required(),
        cpf: yup.string().trim().required().validCPF(),
    });

    const vendedorService = useVendedorService();

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    vendedorService.editar(vendedor.vendedorId, values)
                        .then(response => {
                            message.success("Vendedor editado com sucesso");
                            modal.close();
                            onVendedorAtualizado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [form, loading, message, modal, onVendedorAtualizado, validation, vendedor.vendedorId, vendedorService]);

    const handleEnter = React.useCallback(() => {
        form.reset();
    }, [form]);

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            size="sm"
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle 
                title={`Edição de Vendedor`}
            />

            <ModalWindowContent>
                <VendedorEditarForm 
                    form={form}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Editar Vendedor</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}